/**
 * As notas do time, portadas de app.js:173-320.
 *
 * O desenho é o mesmo, e as regras que importam continuam de pé: a nota mora na
 * âncora do trecho que ela comenta; se a doc for regerada com outro texto, a
 * âncora não casa mais e a nota aparece em "sem âncora — o trecho citado mudou",
 * em vez de desaparecer ou colar no bloco errado. Nota sem âncora é nota geral
 * do documento, e continua no topo.
 *
 * Erro de leitura não vira "não há notas": a faixa avisa que a lista pode estar
 * incompleta, porque afirmar ausência sem ter lido é mentira do painel.
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apagar, postar, remendar } from '../api/cliente';
import { chaves, useNotas } from '../api/consultas';
import type { Nota } from '../api/tipos';
import { analisar, md, semNotasDoTime, type Bloco } from '../md/markdown';
import { useSessao } from '../auth/sessao';
import { useRecado } from '../componentes/Recado';
import { Carregando } from '../componentes/Estado';

export const ESPECIES: Record<string, string> = {
  detalhe: 'detalhe',
  correcao: 'correção',
  runtime: 'runtime',
  gotcha: 'gotcha',
};

/** Markdown já renderizado do acervo; o escape acontece em markdown.ts. */
const Md = ({ texto, className }: { texto?: string | null; className?: string }) => (
  <div className={className} dangerouslySetInnerHTML={{ __html: md(texto) }} />
);

interface Acoes {
  responder: (n: Nota) => void;
  editar: (n: Nota) => void;
  excluir: (n: Nota) => void;
}

function CartaoDeNota({ n, docSha, resposta, acoes, editando, respondendo }: {
  n: Nota;
  docSha?: string | null;
  resposta: boolean;
  acoes: Acoes;
  editando: boolean;
  respondendo: boolean;
}) {
  const { eu } = useSessao();
  const meu = !!eu && (eu.role === 'admin' || (!!n.author_email && n.author_email === eu.email));
  const velha = n.source_sha && docSha && n.source_sha !== docSha;
  return (
    <div className={`note-card${resposta ? ' reply' : ''}`} data-note={n.id}>
      <div className="note-head">
        <span className={`note-kind nk-${n.kind || 'detalhe'}`}>
          {ESPECIES[String(n.kind)] || n.kind || 'detalhe'}
        </span>
        <span className="note-meta">
          {n.author_email || '—'} · {(n.created_at || '').slice(0, 10)}
        </span>
        {velha && (
          <span className="note-stale" title="a doc mudou desde que esta nota foi escrita">
            escrita em @{(n.source_sha || '').slice(0, 7)} · doc @{(docSha || '').slice(0, 7)}
          </span>
        )}
        <span className="note-acts">
          {!resposta && (
            <button type="button" onClick={() => acoes.responder(n)}>responder</button>
          )}
          {meu && <button type="button" onClick={() => acoes.editar(n)}>editar</button>}
          {meu && <button type="button" onClick={() => acoes.excluir(n)}>excluir</button>}
        </span>
      </div>
      {editando || respondendo ? null : <Md className="note-body" texto={n.body_md} />}
    </div>
  );
}

export interface EnvioDeNota {
  corpo: string;
  especie: string;
}

/**
 * O formulário de nota. Ctrl+Enter publica — é o atalho de quem escreve sem
 * tirar a mão do teclado, e estava em app.js:259.
 */
export function Compositor({ citacao, valor, especie, rotulo, dica, aoEnviar, aoCancelar }: {
  citacao?: string | null;
  valor?: string;
  especie?: string;
  rotulo?: string;
  dica?: string;
  aoEnviar: (e: EnvioDeNota) => Promise<void>;
  aoCancelar: () => void;
}) {
  const avisar = useRecado();
  const [corpo, setCorpo] = useState(valor || '');
  const [esp, setEsp] = useState(especie || 'detalhe');
  const [salvando, setSalvando] = useState(false);

  const enviar = async () => {
    if (!corpo.trim()) { avisar('escreva alguma coisa'); return; }
    setSalvando(true);
    try { await aoEnviar({ corpo: corpo.trim(), especie: esp }); }
    finally { setSalvando(false); }
  };

  return (
    <div className="note-form">
      {citacao && (
        <div className="quote">sobre: {citacao.replace(/\s+/g, ' ').slice(0, 140)}</div>
      )}
      <textarea
        autoFocus
        value={corpo}
        onChange={(e) => setCorpo(e.target.value)}
        onKeyDown={(e) => { if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') void enviar(); }}
        placeholder={dica || 'O que o código não mostra: comportamento em produção, regra de negócio, gotcha, correção da observação da IA…'}
      />
      <div className="note-form-row">
        <select value={esp} onChange={(e) => setEsp(e.target.value)} aria-label="tipo da nota">
          {Object.entries(ESPECIES).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <button type="button" className="btn sm" disabled={salvando} onClick={() => void enviar()}>
          {salvando ? 'salvando…' : (rotulo || 'Publicar nota')}
        </button>
        <button type="button" className="btn ghost sm" onClick={aoCancelar}>cancelar</button>
        <span className="muted" style={{ fontSize: 11, marginLeft: 'auto' }}>
          markdown · entra no pacote que o agente lê
        </span>
      </div>
    </div>
  );
}

type Alvo =
  | { tipo: 'bloco'; id: string }
  | { tipo: 'geral' }
  | { tipo: 'resposta'; nota: Nota }
  | { tipo: 'edicao'; nota: Nota }
  | null;

export function CorpoComNotas({ slug, unidade, docSha, corpo, rotuloDaFaixa, vazioDoCorpo }: {
  slug: string;
  unidade: string;
  docSha?: string | null;
  corpo?: string | null;
  rotuloDaFaixa: string;
  vazioDoCorpo?: React.ReactNode;
}) {
  const avisar = useRecado();
  const fila = useQueryClient();
  const consulta = useNotas(slug, unidade);
  const [alvo, setAlvo] = useState<Alvo>(null);

  const blocos = analisar(semNotasDoTime(corpo));
  const notas = consulta.data?.notes || [];

  const recarregar = async () => {
    await Promise.all([
      fila.invalidateQueries({ queryKey: chaves.notas(slug, unidade) }),
      fila.invalidateQueries({ queryKey: chaves.notas(slug, null) }),
    ]);
  };

  const criar = useMutation({
    mutationFn: (corpoDaNota: Record<string, unknown>) =>
      postar<{ id?: string }>(`/api/${slug}/notes`, corpoDaNota),
  });
  const editar = useMutation({
    mutationFn: ({ id, ...resto }: { id: string; body_md: string; kind: string }) =>
      remendar(`/api/${slug}/notes/${id}`, resto),
  });
  const excluir = useMutation({
    mutationFn: (id: string) => apagar(`/api/${slug}/notes/${id}`),
  });

  const acoes: Acoes = {
    responder: (n) => setAlvo({ tipo: 'resposta', nota: n }),
    editar: (n) => setAlvo({ tipo: 'edicao', nota: n }),
    excluir: async (n) => {
      // mesma confirmação do painel atual: excluir a nota leva as respostas
      if (!window.confirm('Excluir esta nota (e as respostas)?')) return;
      try {
        await excluir.mutateAsync(n.id);
        avisar('nota excluída');
        await recarregar();
      } catch (e) { avisar(e instanceof Error ? e.message : String(e)); }
    },
  };

  const publicar = async (dados: Record<string, unknown>, recado: string) => {
    try {
      await criar.mutateAsync(dados);
      avisar(recado);
      setAlvo(null);
      await recarregar();
    } catch (e) { avisar(e instanceof Error ? e.message : String(e)); }
  };

  const respostas = new Map<string, Nota[]>();
  for (const n of notas) {
    if (!n.parent_id) continue;
    if (!respostas.has(n.parent_id)) respostas.set(n.parent_id, []);
    respostas.get(n.parent_id)!.push(n);
  }
  const raizes = notas.filter((n) => !n.parent_id);
  const ancoras = new Set(blocos.map((b) => b.id));
  const porBloco = new Map<string, Nota[]>();
  const soltas: Nota[] = [];
  for (const n of raizes) {
    if (n.anchor && ancoras.has(n.anchor)) {
      if (!porBloco.has(n.anchor)) porBloco.set(n.anchor, []);
      porBloco.get(n.anchor)!.push(n);
    } else soltas.push(n);
  }
  const orfas = soltas.filter((n) => n.anchor);
  const gerais = soltas.filter((n) => !n.anchor);

  const Fio = ({ n }: { n: Nota }) => (
    <div key={n.id}>
      <CartaoDeNota
        n={n}
        docSha={docSha}
        resposta={false}
        acoes={acoes}
        editando={alvo?.tipo === 'edicao' && alvo.nota.id === n.id}
        respondendo={false}
      />
      {alvo?.tipo === 'edicao' && alvo.nota.id === n.id && (
        <Compositor
          valor={n.body_md}
          especie={String(n.kind || 'detalhe')}
          rotulo="Salvar"
          aoCancelar={() => setAlvo(null)}
          aoEnviar={async ({ corpo: c, especie }) => {
            try {
              await editar.mutateAsync({ id: n.id, body_md: c, kind: especie });
              avisar('nota atualizada');
              setAlvo(null);
              await recarregar();
            } catch (e) { avisar(e instanceof Error ? e.message : String(e)); }
          }}
        />
      )}
      {(respostas.get(n.id) || []).map((r) => (
        <CartaoDeNota
          key={r.id}
          n={r}
          docSha={docSha}
          resposta
          acoes={acoes}
          editando={alvo?.tipo === 'edicao' && alvo.nota.id === r.id}
          respondendo={false}
        />
      ))}
      {alvo?.tipo === 'resposta' && alvo.nota.id === n.id && (
        <Compositor
          dica="Responder…"
          rotulo="Responder"
          aoCancelar={() => setAlvo(null)}
          aoEnviar={({ corpo: c, especie }) => publicar({
            unit_key: unidade,
            anchor: n.anchor,
            anchor_kind: n.anchor_kind,
            anchor_quote: n.anchor_quote,
            parent_id: n.id,
            kind: especie,
            body_md: c,
          }, 'resposta publicada')}
        />
      )}
    </div>
  );

  return (
    <>
      <div className="notes-strip">
        <b><span className="n-count">{notas.length}</span> notas do time</b>
        <span className="muted" style={{ fontSize: 12.5 }}>
          {rotuloDaFaixa} · clique no <b>+</b> ao lado de um trecho para comentar nele{' '}
          <span className="kbd">Ctrl</span>+<span className="kbd">Enter</span> publica
        </span>
        <button type="button" className="btn ghost sm" onClick={() => setAlvo({ tipo: 'geral' })}>
          Nota geral
        </button>
      </div>

      <div className="notes-loose">
        {alvo?.tipo === 'geral' && (
          <Compositor
            dica="Nota geral deste documento (sem trecho específico)"
            aoCancelar={() => setAlvo(null)}
            aoEnviar={({ corpo: c, especie }) => publicar({
              unit_key: unidade, anchor_kind: 'doc', kind: especie, body_md: c,
            }, 'nota publicada')}
          />
        )}
        {consulta.isPending && <Carregando rotulo="as notas do time" />}
        {consulta.error && (
          <div className="warn-line">
            As notas do time não carregaram ({consulta.error instanceof Error ? consulta.error.message : String(consulta.error)}).
            O que aparece abaixo pode estar incompleto.
          </div>
        )}
        {gerais.map((n) => <Fio key={n.id} n={n} />)}
        {!!orfas.length && (
          <div className="note-group">
            <h4>sem âncora — o trecho citado mudou</h4>
            {orfas.map((n) => <Fio key={n.id} n={n} />)}
          </div>
        )}
        {!consulta.isPending && !consulta.error && !notas.length && (
          <div className="blank">
            <h4>Ninguém anotou nada aqui ainda</h4>
            <p>
              A IA descreve o que o código faz. O que ela não consegue ver — comportamento em
              produção, regra de negócio, armadilha conhecida — só entra se um humano escrever.
              Notas viajam junto no <code>eng context</code> e valem mais que a prosa gerada.
            </p>
            <div className="how"><code>eng note {unidade} "..."</code></div>
          </div>
        )}
      </div>

      {blocos.length ? (
        <div className="md">
          {blocos.map((b) => (
            <BlocoComNota
              key={b.id}
              b={b}
              notas={porBloco.get(b.id) || []}
              abrindo={alvo?.tipo === 'bloco' && alvo.id === b.id}
              aoComentar={() => setAlvo({ tipo: 'bloco', id: b.id })}
              aoCancelar={() => setAlvo(null)}
              aoEnviar={({ corpo: c, especie }) => publicar({
                unit_key: unidade,
                anchor: b.id,
                anchor_kind: b.especie,
                anchor_quote: b.citacao,
                kind: especie,
                body_md: c,
              }, 'nota publicada — já vale para o agente')}
              Fio={Fio}
            />
          ))}
        </div>
      ) : vazioDoCorpo}
    </>
  );
}

function BlocoComNota({ b, notas, abrindo, aoComentar, aoCancelar, aoEnviar, Fio }: {
  b: Bloco;
  notas: Nota[];
  abrindo: boolean;
  aoComentar: () => void;
  aoCancelar: () => void;
  aoEnviar: (e: EnvioDeNota) => Promise<void>;
  Fio: (p: { n: Nota }) => React.ReactElement;
}) {
  return (
    <div className={`mdb${notas.length ? ' has-note' : ''}`} data-block={b.id} data-kind={b.especie}>
      <button type="button" className="mdb-add" title="Comentar este trecho" onClick={aoComentar}>+</button>
      <div className="mdb-body" dangerouslySetInnerHTML={{ __html: b.html }} />
      <div className="mdb-notes" data-for={b.id}>
        {notas.map((n) => <Fio key={n.id} n={n} />)}
        {abrindo && (
          <Compositor citacao={b.citacao} aoCancelar={aoCancelar} aoEnviar={aoEnviar} />
        )}
      </div>
    </div>
  );
}
